import { buildFunctionUrl } from '../utils';

export const CONNECTOR_OAUTH_MESSAGE_TYPE = 'restack:connector-oauth';
export const CONNECTOR_REQUEST_TIMEOUT_MS = 20000;

const CONNECTOR_ID_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;

// OAuth navigation gets a second client-side pin. This is deliberately a
// code-owned security allowlist rather than provider metadata from an API
// response. Adding a future OAuth connector requires a reviewed code change.
const REVIEWED_OAUTH_ORIGINS = Object.freeze({
  daloopa: Object.freeze(['https://mcp.daloopa.com']),
});

const PUBLIC_ERROR_COPY = Object.freeze({
  feature_disabled: () => 'Finance connectors are not enabled for this Restack environment.',
  connector_disabled: (name) => `${name} is not enabled for this organization.`,
  not_entitled: () => 'Your organization does not have access to finance connectors.',
  plan_not_entitled: () => 'Your organization does not have access to finance connectors.',
  org_not_enabled: (name) => `${name} is not enabled for this organization.`,
  contract_not_approved: (name) => `${name} is awaiting vendor or contract approval for this organization.`,
  invalid_connector_config: (name) => `${name} is not configured correctly for this Restack environment.`,
  forbidden: () => 'You do not have permission to manage this connector.',
  oauth_denied: (name) => `${name} authorization was cancelled or denied.`,
  oauth_state_invalid: () => 'This authorization link is no longer valid. Start the connection again.',
  oauth_exchange_failed: (name) => `${name} could not be connected. Start the connection again.`,
  credential_invalid: (name) => `${name} needs to be reconnected.`,
  auth_expired: (name) => `${name} needs to be reconnected.`,
  credential_missing: (name) => `${name} is not connected.`,
  credential_corrupt: (name) => `${name} needs to be reconnected.`,
  connector_not_connected: (name) => `${name} is not connected.`,
  provider_timeout: (name) => `${name} did not respond in time. Try again shortly.`,
  provider_unavailable: (name) => `${name} is temporarily unavailable.`,
  provider_invalid_response: (name) => `${name} returned an invalid response.`,
  provider_response_too_large: (name) => `${name} returned too much data to process safely.`,
  rate_limited: (name) => `${name} is temporarily busy. Try again shortly.`,
  vault_unavailable: () => 'The secure connector vault is temporarily unavailable.',
  audit_unavailable: () => 'The connector call was stopped because its audit record could not be written.',
  connector_not_supported: () => 'This connector is not supported.',
  oauth_state_expired: () => 'This authorization link expired. Start the connection again.',
  oauth_state_used: () => 'This authorization link has already been used. Start the connection again.',
  oauth_code_invalid: (name) => `${name} authorization was invalid. Start the connection again.`,
  oauth_client_unconfigured: (name) => `${name} OAuth is not configured for this Restack environment.`,
  oauth_redirect_invalid: (name) => `${name} callback settings are not configured correctly.`,
  oauth_metadata_unavailable: (name) => `${name} authorization is temporarily unavailable.`,
  oauth_metadata_invalid: (name) => `${name} authorization is not configured correctly.`,
  oauth_pkce_required: (name) => `${name} requires a secure authorization flow that is not configured yet.`,
  oauth_public_client_unsupported: (name) => `${name} OAuth client configuration is not supported.`,
  operation_in_progress: () => 'Connector update in progress.',
  operation_interrupted: () => 'The prior connector update did not finish. Try again.',
  request_timeout: () => 'The connector request timed out. Refresh to confirm its current state.',
  unauthorized: () => 'Sign in again to manage connectors.',
  insufficient_permissions: () => 'You do not have permission to manage this connector.',
  connector_subject_mismatch: () => 'Connector settings could not be verified for this account.',
});

export class ConnectorApiError extends Error {
  constructor(message, { code = null, status = null } = {}) {
    super(message);
    this.name = 'ConnectorApiError';
    this.code = code;
    this.status = status;
  }
}

function safeConnectorName(value) {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return normalized && normalized.length <= 80 ? normalized : 'This connector';
}

function safeErrorMessage(status, code, connectorName) {
  if (code && Object.prototype.hasOwnProperty.call(PUBLIC_ERROR_COPY, code)) {
    return PUBLIC_ERROR_COPY[code](safeConnectorName(connectorName));
  }
  if (status === 401) return 'Sign in again to manage connectors.';
  if (status === 403) return PUBLIC_ERROR_COPY.forbidden();
  if (status === 404) return 'Finance connectors are not available yet.';
  if (status === 429) return PUBLIC_ERROR_COPY.rate_limited(safeConnectorName(connectorName));
  if (status >= 500) return 'Connector settings are temporarily unavailable.';
  return 'The connector request could not be completed.';
}

export function getConnectorPublicErrorMessage(code, connectorName) {
  return code && Object.prototype.hasOwnProperty.call(PUBLIC_ERROR_COPY, code)
    ? PUBLIC_ERROR_COPY[code](safeConnectorName(connectorName))
    : 'The connector request could not be completed.';
}

function requireConnectorId(value) {
  if (typeof value !== 'string' || !CONNECTOR_ID_PATTERN.test(value)) {
    throw new ConnectorApiError('Choose a supported connector.', {
      code: 'connector_not_supported',
      status: 400,
    });
  }
  return value;
}

function abortError() {
  const error = new Error('The connector request was cancelled.');
  error.name = 'AbortError';
  return error;
}

function requestDeadline(externalSignal, timeoutMs) {
  const controller = new AbortController();
  let timedOut = false;
  const abortFromCaller = () => controller.abort();
  if (externalSignal?.aborted) controller.abort();
  else externalSignal?.addEventListener('abort', abortFromCaller, { once: true });
  const boundedTimeoutMs = Math.max(
    1000,
    Math.min(60000, Number(timeoutMs) || CONNECTOR_REQUEST_TIMEOUT_MS)
  );
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, boundedTimeoutMs);
  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    cleanup() {
      clearTimeout(timeout);
      externalSignal?.removeEventListener('abort', abortFromCaller);
    },
  };
}

async function connectorRequest(
  currentUser,
  endpoint,
  {
    body = {},
    signal,
    timeoutMs = CONNECTOR_REQUEST_TIMEOUT_MS,
    connectorName = null,
  } = {}
) {
  if (!currentUser?.getIdToken) {
    throw new ConnectorApiError('Sign in again to manage connectors.', { status: 401 });
  }
  const deadline = requestDeadline(signal, timeoutMs);
  try {
    const token = await Promise.race([
      currentUser.getIdToken(),
      new Promise((_, reject) => {
        deadline.signal.addEventListener('abort', () => reject(abortError()), { once: true });
      }),
    ]);
    if (typeof token !== 'string' || !token.trim()) {
      throw new ConnectorApiError('Sign in again to manage connectors.', { status: 401 });
    }
    const response = await fetch(buildFunctionUrl(endpoint), {
      method: 'POST',
      signal: deadline.signal,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const code = typeof data?.code === 'string' ? data.code : null;
      throw new ConnectorApiError(
        safeErrorMessage(response.status, code, connectorName),
        { code, status: response.status }
      );
    }
    return data && typeof data === 'object' ? data : {};
  } catch (error) {
    if (deadline.timedOut()) {
      throw new ConnectorApiError(PUBLIC_ERROR_COPY.request_timeout(), {
        code: 'request_timeout',
        status: 408,
      });
    }
    if (signal?.aborted || error?.name === 'AbortError') throw abortError();
    throw error;
  } finally {
    deadline.cleanup();
  }
}

export function connectorConsoleReturnUrl(location = null) {
  const activeLocation = location || (typeof window !== 'undefined' ? window.location : null);
  if (!activeLocation?.origin) return null;
  return new URL('/settings', activeLocation.origin).href;
}

export function validateAuthorizationUrl(value, connectorId) {
  const reviewedOrigins = REVIEWED_OAUTH_ORIGINS[requireConnectorId(connectorId)];
  if (!Array.isArray(reviewedOrigins) || reviewedOrigins.length === 0) {
    throw new ConnectorApiError('This connector does not have a reviewed OAuth destination.');
  }
  let parsed;
  try {
    parsed = new URL(String(value || ''));
  } catch {
    throw new ConnectorApiError('The connector returned an invalid authorization link.');
  }
  if (parsed.protocol !== 'https:'
    || !reviewedOrigins.includes(parsed.origin)
    || parsed.username
    || parsed.password) {
    throw new ConnectorApiError('The connector returned an invalid authorization link.');
  }
  return parsed.toString();
}

export const getOrgConnectors = (currentUser, options = {}) =>
  connectorRequest(currentUser, 'getOrgConnectors', options);

export const getMyConnectors = (currentUser, options = {}) =>
  connectorRequest(currentUser, 'getMyConnectors', options);

export const getMyConnectorReadiness = (currentUser, connectorId, options = {}) =>
  connectorRequest(currentUser, 'getMyConnectorReadiness', {
    body: { connectorId: requireConnectorId(connectorId) },
    signal: options.signal,
    timeoutMs: options.timeoutMs,
    connectorName: options.connectorName,
  });

export const beginConnectorOAuth = (currentUser, connectorId, options = {}) =>
  connectorRequest(currentUser, 'beginConnectorOAuth', {
    body: {
      connectorId: requireConnectorId(connectorId),
      consoleReturnUrl: options.consoleReturnUrl || connectorConsoleReturnUrl(),
    },
    signal: options.signal,
    timeoutMs: options.timeoutMs,
    connectorName: options.connectorName,
  });

export const completeConnectorOAuth = (
  currentUser,
  { connectorId, code, state } = {},
  options = {}
) => connectorRequest(currentUser, 'completeConnectorOAuth', {
  body: {
    connectorId: requireConnectorId(connectorId),
    code,
    state,
  },
  signal: options.signal,
  timeoutMs: options.timeoutMs,
  connectorName: options.connectorName,
});

export const validateConnector = (currentUser, connectorId, options = {}) =>
  connectorRequest(currentUser, 'validateConnector', {
    body: { connectorId: requireConnectorId(connectorId) },
    signal: options.signal,
    timeoutMs: options.timeoutMs,
    connectorName: options.connectorName,
  });

export const disconnectConnector = (currentUser, connectorId, options = {}) =>
  connectorRequest(currentUser, 'disconnectConnector', {
    body: { connectorId: requireConnectorId(connectorId) },
    signal: options.signal,
    timeoutMs: options.timeoutMs,
    connectorName: options.connectorName,
  });

const connectorService = {
  getOrgConnectors,
  getMyConnectors,
  getMyConnectorReadiness,
  beginConnectorOAuth,
  completeConnectorOAuth,
  validateConnector,
  disconnectConnector,
  getConnectorPublicErrorMessage,
  validateAuthorizationUrl,
};

export default connectorService;
